import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export interface NoteSessionMetadata {
  clientName?: string;
  sessionDate?: string;
  sessionTime?: string;
  duration?: string;
  sessionType?: string;
}

interface GenerateNoteRequest {
  transcript: string;
  template: string;
  sessionMetadata?: NoteSessionMetadata;
}

interface GenerateNoteResponse {
  success: boolean;
  data?: Record<string, string>;
  error?: string;
}

/**
 * Calls the generateNote Cloud Function to parse a dictation transcript into
 * the selected template's fields. 30s per-call timeout; retries once on
 * failure. The Anthropic key lives only in the Cloud Function.
 */
class NoteGenerationService {
  async generate(
    transcript: string,
    template: string,
    sessionMetadata?: NoteSessionMetadata
  ): Promise<Record<string, string>> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('You must be logged in to generate notes');
    }

    const functions = getFunctions();
    const callable = httpsCallable<GenerateNoteRequest, GenerateNoteResponse>(
      functions,
      'generateNote',
      { timeout: 30000 } // 30s timeout per attempt
    );

    const attempt = async (): Promise<Record<string, string>> => {
      const res = await callable({ transcript, template, sessionMetadata });
      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.error || 'Note generation failed');
      }
      return res.data.data;
    };

    try {
      return await attempt();
    } catch (err) {
      console.error('generateNote failed, retrying once:', err);
      return await attempt(); // retry once
    }
  }
}

export const noteGenerationService = new NoteGenerationService();
